import { Fragment, useCallback, useEffect, useState } from "react";
import { Control, Controller, UseFormGetValues, UseFormSetValue, UseFormTrigger, RegisterOptions, FieldPath } from "react-hook-form";
import { useKeyPress } from "hooks/useKeyPress";
import { BaseButton } from "components/buttons/BaseButton";
import { ISignUpFieldValues } from "../AuthSignUp";
import { apiEmail } from "services";
import { AxiosError } from "axios";
import { IErrorData } from "services/types";
import { TextInput } from "components/inputs/TextInput/TextInput";
import { AuthPopupHelpButton } from "../AuthPopupHelpButton";
import authPopupClasses from "./../AuthPopup.module.scss";

interface IProps {
  next: () => void;
  back: () => void;
  control: Control<ISignUpFieldValues>;
  trigger: UseFormTrigger<ISignUpFieldValues>;
  setValue: UseFormSetValue<ISignUpFieldValues>;
  getValues: UseFormGetValues<ISignUpFieldValues>;
}

const CODE_LENGTH = 6;

const SignUpCodeStep = (props: IProps) => {
  const { next, back, control, trigger, setValue, getValues } = props;

  const [disabled, setDisabled] = useState(false);

  const enterPressed = useKeyPress("Enter");

  const handleNext = useCallback(async () => {
    if (await trigger("validation_code")) {
      next();
    }
  }, [next, trigger]);

  const handleResendCode = () => {
    back();
  };

  useEffect(() => {
    if (enterPressed) {
      handleNext();
    }
  }, [enterPressed, handleNext]);

  return (
    <div className={authPopupClasses.ContentContainer}>
      <p>Enter 6-digits code:</p>
      <Controller
        name="validation_code"
        control={control}
        defaultValue=""
        rules={{
          required: {
            value: true,
            message: "Required Field!"
          },
          validate: {
            value: async (value: string) => {
              if (value.length === CODE_LENGTH) {
                setDisabled(true);

                try {
                  await apiEmail.checkCode({
                    token: getValues().validation_token,
                    code: value
                  });
                  setValue<FieldPath<ISignUpFieldValues>>("is_confirm_email", true);
                  next();
                } catch (e) {
                  console.warn(e);
                  setValue<FieldPath<ISignUpFieldValues>>("is_confirm_email", false);
                  return (e as AxiosError<IErrorData>).response?.data?.error?.message;
                } finally {
                  setDisabled(false);
                }
              }

              return false;
            }
          }
        } as RegisterOptions }
        render={({ field, fieldState }) => (
          <Fragment>
            <TextInput
              label={`Your code was sent to ${getValues().email}`}
              onChange={async (e) => {
                field.onChange(e);
                await trigger("validation_code");
              }}
              value={field.value}
              error={fieldState.error ? fieldState.error.message : ""}
            />
            <AuthPopupHelpButton onClick={handleResendCode} value="Resend Code"/>
          </Fragment>
        )}
      />

      <div className={authPopupClasses.Footer}>
        <BaseButton type="button" onClick={handleNext} disabled={disabled}>Next</BaseButton>
      </div>
    </div>
  );
};

export default SignUpCodeStep;
